import { useCallback, useEffect, useRef, useState } from 'react'
import { generateId } from '../lib/utils'
import { streamChat } from '../lib/openrouter'
import { runContextPipeline } from '../lib/context-pipeline'
import type { Message, Artifact, StreamChunk, ContextPipelineProgress, Conversation, TokenUsage } from '../lib/types'
import type { ContextPipelineResult } from '../lib/context-pipeline'
import { supabase } from '../lib/supabase'
import { logger } from '../lib/logger'

interface UseChatOptions {
  model?: string
  apiKey?: string
  mode?: 'learning' | 'debating'
  userName?: string
  userReligion?: string
  userBelief?: string
  conversations?: Conversation[]
  userId?: string
  conversationId?: string
  onConversationCreated?: (id: string) => void
}

export function useChat({ model, apiKey, mode, userName, userReligion, userBelief, conversations, userId, conversationId, onConversationCreated }: UseChatOptions = {}) {
  const [messages, setMessages] = useState<Message[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pipelineProgress, setPipelineProgress] = useState<ContextPipelineProgress | null>(null)
  const [lastPipeline, setLastPipeline] = useState<ContextPipelineResult | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const hasSentRef = useRef(false)

  // Load existing messages when conversationId changes
  useEffect(() => {
    if (!conversationId) return
    let cancelled = false
    const loadMessages = async () => {
      logger.info('Loading conversation messages', { conversationId })
      try {
        const { data } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('session_id', conversationId)
          .order('created_at', { ascending: true })
        if (!cancelled && data) {
          const msgs = data.map(m => ({
            id: m.id,
            role: m.role,
            content: m.content,
            thinking: m.thinking || undefined,
            artifacts: m.artifacts || [],
            timestamp: new Date(m.created_at).getTime(),
            usage: m.usage || undefined,
          }))
          setMessages(msgs)
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load conversation')
          logger.captureException(err, { context: 'loadMessages', conversationId })
        }
      }
    }
    loadMessages()
    return () => { cancelled = true }
  }, [conversationId])

  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  // Reset sent flag when conversation changes
  useEffect(() => {
    hasSentRef.current = false
  }, [conversationId])

  const sendMessage = useCallback(async (content: string) => {
    setError(null)
    setPipelineProgress(null)
    logger.info('Sending message', {
      contentLength: content.length,
      contentPreview: content.slice(0, 80),
      conversationId,
      mode,
    })

    // Register conversation in DB on first message
    if (!hasSentRef.current && conversationId && onConversationCreated) {
      onConversationCreated(conversationId)
    }
    hasSentRef.current = true

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: Date.now(),
    }
    setMessages(prev => [...prev, userMessage])
    setIsStreaming(true)

    const onPipelineProgress = (progress: ContextPipelineProgress) => setPipelineProgress(progress)
    const assistantId = generateId()
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      artifacts: [],
    }
    setMessages(prev => [...prev, assistantMessage])

    const abortController = new AbortController()
    abortRef.current = abortController

    try {
      logger.info('Running context pipeline', { mode, userName: !!userName })
      const pipeline = await runContextPipeline(content, onPipelineProgress, { userName, userReligion, userBelief, mode })
      setLastPipeline(pipeline)
      logger.debug('Pipeline completed', {
        transcriptChunks: pipeline.transcriptResults.length,
        vectorContextLength: pipeline.vectorContext.length,
        hasTranscriptContext: !!pipeline.transcriptContext,
      })

      const recentConvs = (conversations || [])
        .filter(c => c.id !== 'new')
        .slice(0, 10)
        .map(c => `- "${c.title || 'Untitled'}" (${c.mode || 'unknown'} mode, ${new Date(c.updatedAt).toLocaleDateString()})`)
        .join('\n')

      const convContext = conversations && conversations.length > 0
        ? `\n\n===== USER'S RECENT CONVERSATIONS =====\nThe user has ${conversations.length} past conversations. Recent ones:\n${recentConvs}\n\nYou can use the "get_conversation_history" tool to read any past conversation by ID, or "search_conversations" to find relevant past discussions.`
        : ''

      const pipelineWithConvs = pipeline.combinedSystemPrompt + convContext

      const history = messages.concat(userMessage).map(m => ({
        role: m.role,
        content: m.content,
      }))

      let streamedContent = ''
      const artifacts: Artifact[] = []
      let reasoningTokens = ''
      let finalUsage: TokenUsage | undefined

      logger.info('Starting streaming chat', { model, historyLength: history.length })
      await streamChat(
        {
          messages: history,
          systemPrompt: pipelineWithConvs,
          relevantTranscripts: pipeline.transcriptContext || undefined,
          vectorContext: pipeline.vectorContext || undefined,
          model,
        },
        (chunk: StreamChunk) => {
          if (chunk.type === 'text') {
            streamedContent += chunk.content || ''
            setMessages(prev =>
              prev.map(msg => msg.id === assistantId ? { ...msg, content: streamedContent } : msg)
            )
          } else if (chunk.type === 'thought') {
            reasoningTokens += (reasoningTokens ? '\n' : '') + (chunk.content || '')
            setMessages(prev =>
              prev.map(msg => msg.id === assistantId ? { ...msg, thinking: reasoningTokens } : msg)
            )
          } else if (chunk.type === 'artifact' && chunk.content && chunk.title) {
            const artifact: Artifact = {
              id: chunk.id || generateId(),
              title: chunk.title || 'Artifact',
              content: chunk.content,
              type: (chunk.artifactType as Artifact['type']) || 'outline',
            }
            artifacts.push(artifact)
            setMessages(prev =>
              prev.map(msg =>
                msg.id === assistantId ? { ...msg, artifacts: [...(msg.artifacts || []), artifact] } : msg
              )
            )
          } else if (chunk.type === 'error') {
            setError(chunk.content || 'Unknown error')
          } else if (chunk.type === 'usage' && chunk.usage) {
            finalUsage = chunk.usage
          } else if (chunk.type === 'tool_step') {
            setPipelineProgress({ phase: 'tool-call', message: chunk.content || `Searching (${chunk.step}/${chunk.totalSteps})...` })
          } else if (chunk.type === 'tool_call') {
            const names = chunk.tool_calls?.map(t => {
              const name = t.name === 'search_quran' ? 'Quran' : t.name === 'search_bible' ? 'Bible' : t.name === 'search_hadith' ? 'Hadith' : t.name === 'search_debate_transcripts' ? 'Transcripts' : t.name === 'get_raw_text' ? 'Raw Text' : t.name === 'list_raw_files' ? 'Raw Files' : t.name
              return name
            }).join(', ')
            setPipelineProgress({ phase: 'tool-call', message: `Searching ${names}...` })
          } else if (chunk.type === 'tool_result') {
            setPipelineProgress({ phase: 'tool-call', message: 'Analyzing results...' })
          } else if (chunk.type === 'cost' && chunk.cost !== undefined) {
            const prevTotal = parseFloat(localStorage.getItem('islam-preacher-total-cost') || '0')
            const newTotal = prevTotal + chunk.cost
            localStorage.setItem('islam-preacher-total-cost', newTotal.toFixed(6))
          }
        },
        abortController.signal,
        apiKey,
        userId ? { supabase, userId, conversations } : undefined
      )

      const finalContent = streamedContent
      let thinking = reasoningTokens
      let cleanContent = finalContent
      let allArtifacts = [...artifacts]

      const thoughtRegex = /<thought>([\s\S]*?)<\/thought>/g
      let match
      while ((match = thoughtRegex.exec(finalContent)) !== null) {
        thinking += (thinking ? '\n\n' : '') + match[1]
      }
      cleanContent = finalContent.replace(/<thought>[\s\S]*?<\/thought>/g, '')

      const artifactRegex = /<artifact\s+(?:type="([^"]*)")?\s*(?:title="([^"]*)")?>([\s\S]*?)<\/artifact>/g
      while ((match = artifactRegex.exec(finalContent)) !== null) {
        allArtifacts.push({
          id: generateId(),
          title: match[2] || 'Artifact',
          content: match[3],
          type: (match[1] as Artifact['type']) || 'outline',
        })
      }
      cleanContent = cleanContent.replace(/<artifact\s+[^>]*>[\s\S]*?<\/artifact>/g, '')

      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantId
            ? {
                ...msg,
                content: cleanContent.trim(),
                thinking: thinking.trim() || undefined,
                artifacts: allArtifacts.length > 0 ? allArtifacts : undefined,
                pipelineInfo: pipeline.pipelineInfo,
                usage: finalUsage,
              }
            : msg
        )
      )
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        logger.info('Message generation aborted')
        return
      }
      const msg = err instanceof Error ? err.message : 'Failed to send message'
      logger.captureException(err, { context: 'sendMessage', contentPreview: content.slice(0, 80) })
      setError(msg)
    } finally {
      setIsStreaming(false)
      setPipelineProgress(null)
      abortRef.current = null
      logger.info('Message generation complete', {
        hasError: !!error,
        messageCount: messages.length,
      })
    }
  }, [messages, model, apiKey, userName, userReligion, userBelief, mode, conversations, userId, conversationId, onConversationCreated])

  const stopGeneration = useCallback(() => {
    abortRef.current?.abort()
    setIsStreaming(false)
    setPipelineProgress(null)
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
    setPipelineProgress(null)
  }, [])

  const updateMessage = useCallback((id: string, updates: Partial<Message>) => {
    setMessages(prev =>
      prev.map(msg => msg.id === id ? { ...msg, ...updates } : msg)
    )
  }, [])

  const regenerateLast = useCallback(() => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')
    if (!lastUserMsg) return
    setMessages(prev => prev.slice(0, -1))
    sendMessage(lastUserMsg.content)
  }, [messages, sendMessage])

  const deleteMessage = useCallback((id: string) => {
    setMessages(prev => prev.filter(m => m.id !== id))
    if (conversationId) {
      supabase.from('chat_messages').delete().eq('id', id).then()
    }
  }, [conversationId])

  return { messages, isStreaming, error, pipelineProgress, sendMessage, regenerateLast, deleteMessage, stopGeneration, clearMessages, updateMessage, lastPipeline }
}
